import {
  Box, Button, Drawer, Grid, TextField, Typography, FormControlLabel, Checkbox, Autocomplete
} from '@mui/material';
import { FC, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import LoadingButton from '@mui/lab/LoadingButton';
import { ApiStatus } from "../../utils/api.types";
import { ICostCentreFormInputs } from '../../utils/costCentre.types';
import { createCostCentre, updateCostCentre, fetchCostCentres } from '../../store/costCentreStore';
import { fetchCostCategories } from '../../store/costCategoryStore';

interface DialogCostCentreFormProps {
  open: boolean;
  onClose: () => void;
  editData?: any;
}

const schema = yup.object().shape({
  name: yup.string().required("Cost Centre Name is required").min(2, "Name must be at least 2 characters").max(100, "Name must not exceed 100 characters"),
  alias: yup.string().max(20, "Alias must not exceed 20 characters"),
  description: yup.string().max(500, "Description must not exceed 500 characters"),
  useForPayroll: yup.boolean(),
  useForPayments: yup.boolean(),
  isActive: yup.boolean()
});

const DialogCostCentreForm: FC<DialogCostCentreFormProps> = ({ onClose, open, editData }) => {
  const { status } = useAppSelector(state => state.costCentres);
  const { categories } = useAppSelector(state => state.costCategories);
  const { costCentres } = useAppSelector(state => state.costCentres);
  const dispatch = useAppDispatch();

  const methods = useForm<ICostCentreFormInputs>({
    mode: 'onTouched',
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      alias: '',
      category: null,
      parent: null,
      description: '',
      useForPayroll: false,
      useForPayments: false,
      isActive: true
    }
  });

  const { handleSubmit, reset, setValue, watch, formState: { errors } } = methods;
  const selectedParentId = watch('parent');

  // Load categories when form opens
  useEffect(() => {
    if (open) {
      dispatch(fetchCostCategories(true));
      dispatch(fetchCostCentres({ activeOnly: false }));
    }
  }, [open, dispatch]);

  // Populate form when editing
  useEffect(() => {
    if (editData) {
      reset({
        name: editData.name || '',
        alias: editData.alias || '',
        category: typeof editData.category === 'object' ? editData.category?._id : editData.category || null,
        parent: typeof editData.parent === 'object' ? editData.parent?._id : editData.parent || null,
        description: editData.description || '',
        useForPayroll: editData.useForPayroll || false,
        useForPayments: editData.useForPayments || false,
        isActive: editData.isActive !== undefined ? editData.isActive : true
      });
    } else {
      reset({
        name: '',
        alias: '',
        category: null,
        parent: null,
        description: '',
        useForPayroll: false,
        useForPayments: false,
        isActive: true
      });
    }
  }, [editData, reset]);

  const onSubmit = async (data: ICostCentreFormInputs) => {
    try {
      if (editData?._id) {
        await dispatch(updateCostCentre({ id: editData._id, data })).unwrap();
      } else {
        await dispatch(createCostCentre(data)).unwrap();
      }
      await dispatch(fetchCostCentres({ activeOnly: true }));
      reset();
      onClose();
    } catch (error) {
      console.error('Error saving cost centre:', error);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  // Filter out self and descendants from parent options (prevent circular reference)
  const availableParents = costCentres.filter(cc => cc._id !== editData?._id);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 600, md: 700 },
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
          }}
        >
          {/* Header */}
          <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {editData ? 'Edit Cost Centre' : 'Add Cost Centre'}
            </Typography>
          </Box>

          {/* Content - Scrollable */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8}>
                <Controller
                  name="name"
                  control={methods.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Cost Centre Name"
                      fullWidth
                      required
                      size="small"
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      placeholder="e.g., Mumbai Branch, Sales Department"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Controller
                  name="alias"
                  control={methods.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Alias (Short Code)"
                      fullWidth
                      size="small"
                      error={!!errors.alias}
                      helperText={errors.alias?.message}
                      placeholder="e.g., MUM, DEL"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="category"
                  control={methods.control}
                  render={({ field: { onChange, value } }) => (
                    <Autocomplete
                      size="small"
                      options={categories}
                      getOptionLabel={(option) => option.name}
                      value={categories.find(cat => cat._id === value) || null}
                      onChange={(_, newValue) => onChange(newValue?._id || null)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Category"
                          placeholder="Select category (optional)"
                        />
                      )}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="parent"
                  control={methods.control}
                  render={({ field: { onChange, value } }) => (
                    <Autocomplete
                      size="small"
                      options={availableParents}
                      getOptionLabel={(option) => `${option.name}${option.alias ? ` (${option.alias})` : ''}`}
                      value={availableParents.find(cc => cc._id === value) || null}
                      onChange={(_, newValue) => onChange(newValue?._id || null)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Parent Cost Centre"
                          placeholder="Select parent (optional)"
                          helperText="For hierarchical structure"
                        />
                      )}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={methods.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Description"
                      fullWidth
                      multiline
                      rows={3}
                      error={!!errors.description}
                      helperText={errors.description?.message}
                      placeholder="Optional description"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="useForPayroll"
                  control={methods.control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value}
                          color="primary"
                        />
                      }
                      label="Use for Payroll Allocation"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="useForPayments"
                  control={methods.control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value}
                          color="primary"
                        />
                      }
                      label="Use for Payment Allocation"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="isActive"
                  control={methods.control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value}
                          color="primary"
                        />
                      }
                      label="Active"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Footer - Fixed at bottom */}
          <Box sx={{
            borderTop: '1px solid #e5e7eb',
            p: 2,
            backgroundColor: '#f9fafb',
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end'
          }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={status === ApiStatus.LOADING}
              sx={{
                minWidth: 100,
                height: 40,
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={status === ApiStatus.LOADING}
              sx={{
                minWidth: 140,
                height: 40,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              {editData ? 'Update' : 'Save'}
            </LoadingButton>
          </Box>
        </Box>
      </FormProvider>
    </Drawer>
  );
};

export default DialogCostCentreForm;
